import * as style from "@client/style";
import {server} from "@reactivated";
import "chartjs-adapter-date-fns";
import {
    Chart,
    Colors,
    Filler,
    LineController,
    LineElement,
    LinearScale,
    PointElement,
    TimeScale,
    Tooltip,
} from "chart.js";
import React from "react";

Chart.register([
    Colors,
    Filler,
    TimeScale,
    LineController,
    LinearScale,
    Tooltip,
    PointElement,
    LineElement,
]);

type Measurement =
    server.manager.templates.ClientManagerReports["measurements"][number];

type Line = {
    measurements: Measurement[];
    trend: Measurement[];
    color?: {line: string; point: {background: string; border: string}};
};

type Props = {
    lines: Array<Line>;
    right?: Line;
};

function prepareLine(line: Line, right?: boolean, fill?: CanvasGradient) {
    const {measurements, trend, color} = line;

    const chartData = measurements.map((measurement) => ({
        x: measurement.date,
        y: measurement.metric != null ? parseFloat(measurement.metric) : null,
    }));

    const trendData = trend.map((measurement) => ({
        x: measurement.date,
        y: measurement.metric != null ? parseFloat(measurement.metric) : null,
    }));
    const lineColor = color ?? style.colors.chart;

    return [
        {
            borderColor: `${lineColor.line}80`, // 80 = 50% opacity
            borderWidth: 2,
            tension: 0,
            data: right === true ? [] : trendData,
            borderDash: [6, 4],
            pointRadius: 0,
            spanGaps: true,
            yAxisID: "y",
        },
        {
            borderColor: lineColor.line,
            borderWidth: 2,
            tension: 0.3,
            data: chartData,
            fill: fill != null,
            backgroundColor: fill,
            pointRadius: 4,
            pointBackgroundColor: "white",
            pointBorderColor: lineColor.point.border,
            pointBorderWidth: 2,
            pointHoverRadius: 6,
            pointHoverBackgroundColor: "#6366F1",
            pointHoverBorderColor: "white",
            pointHoverBorderWidth: 3,
            spanGaps: true,
            yAxisID: right === true ? "y1" : "y",
        },
    ];
}

export const LineChart = (props: Props) => {
    const canvasElement = React.useRef<HTMLCanvasElement>(null);
    const chart = <canvas ref={canvasElement} />;

    React.useEffect(() => {
        const canvas = canvasElement.current!;
        const ctx = canvas.getContext("2d")!;

        // Create gradient for area fill (only used on first line)
        const areaGradient = ctx.createLinearGradient(0, 0, 0, 300);
        areaGradient.addColorStop(0, "rgba(129, 140, 248, 0.3)");
        areaGradient.addColorStop(1, "rgba(129, 140, 248, 0.02)");

        const leftDatasets = props.lines
            .map((line, index) =>
                prepareLine(line, false, index === 0 ? areaGradient : undefined),
            )
            .flat();

        const rightDatasets =
            props.right != null
                ? prepareLine({...props.right, color: style.colors.chart3}, true)
                : [];

        const datasets = [...leftDatasets, ...rightDatasets];

        const chartInstance = new Chart(canvas, {
            type: "line",
            data: {
                datasets,
            },
            options: {
                responsive: true,
                maintainAspectRatio: false,
                layout: {
                    padding: {left: 0, right: 0, top: 0, bottom: 10},
                },
                animation: {
                    duration: 0,
                },
                plugins: {
                    legend: {
                        display: false,
                    },
                    tooltip: {
                        enabled: true,
                        backgroundColor: "white",
                        titleColor: "#94A3B8",
                        titleFont: {
                            family: "InterVariable",
                            size: 12,
                            weight: "bold",
                        },
                        bodyColor: "#1E293B",
                        bodyFont: {
                            family: "InterVariable",
                            size: 16,
                            weight: "normal",
                        },
                        padding: {top: 10, bottom: 10, left: 14, right: 14},
                        cornerRadius: 12,
                        borderColor: "rgba(0,0,0,0.05)",
                        borderWidth: 1,
                        displayColors: false,
                    },
                },
                scales: {
                    x: {
                        type: "time",
                        time: {
                            unit: "day",
                            tooltipFormat: "yyyy-MM-dd",
                        },
                        grid: {
                            display: false,
                        },
                        border: {
                            display: false,
                        },
                        afterBuildTicks: (axis) => {
                            const min = axis.min;
                            const max = axis.max;
                            const range = max - min;
                            // Adjust tick count based on available width
                            const width = axis.width;
                            const tickCount = Math.max(
                                2,
                                Math.min(5, Math.floor(width / 80)),
                            );
                            // Generate evenly spaced ticks including min and max
                            axis.ticks = Array.from({length: tickCount}, (_, i) => ({
                                value: min + (range * i) / (tickCount - 1),
                            }));
                        },
                        ticks: {
                            padding: 0,
                            color: "#666",
                            font: {
                                size: 14,
                                weight: "normal",
                                family: "InterVariable",
                            },
                        },
                    },
                    y: {
                        display: true,
                        grace: "5%",
                        grid: {
                            color: "#F1F5F9",
                        },
                        border: {
                            display: false,
                        },
                        ticks: {
                            color: "#94A3B8",
                            font: {size: 14, weight: "normal", family: "InterVariable"},
                            padding: 12,
                            callback: function (value, index) {
                                // Hide bottommost label but keep the grid line
                                if (index === 0) return "";
                                if (typeof value === "number") {
                                    return parseFloat(value.toFixed(1));
                                }
                                return value;
                            },
                        },
                    },
                    y1: {
                        type: "linear",
                        display: props.right != null,
                        position: "right",
                        grid: {
                            drawOnChartArea: false,
                        },
                        border: {
                            display: false,
                        },
                        ticks: {
                            color: "#94A3B8",
                            font: {size: 11, weight: "normal"},
                            padding: 12,
                            callback: function (value) {
                                if (typeof value === "number") {
                                    return parseFloat(value.toFixed(1));
                                }
                                return value;
                            },
                        },
                    },
                },
            },
        });

        return () => {
            chartInstance.destroy();
        };
    }, [props.lines, props.right]);

    return <>{chart}</>;
};
